import { ReactNode, useState } from 'react';
import JwtDecode from 'jwt-decode';
import { MsalProvider, MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType, RedirectRequest } from '@azure/msal-browser';
import { Config } from '@copilot/common/config';
import { useDispatch } from 'react-redux';
import { AppActions } from '@copilot/common/store/actions/app';
import { LoginResponseToken } from '@copilot/data/responses/interface';
import { OrganizationMemberActions } from '@copilot/common/store/actions/organizationMember';
import { appInsights } from '@copilot/common/components/snippets/applicationInsights';
import { tagManager } from '@copilot/common/components/snippets/googleTagManager';
import { Navigate, useNavigate } from 'react-router-dom';
import { useEffectAsync } from '@copilot/common/hooks/common';
import getAuthToken from '@copilot/common/utils/authToken';
import { AppLoader } from '@copilot/teams/src/components/app-loader/appLoader';
import BaseDataManager from '@copilot/data/managers/base';
import isNil from 'lodash/isNil';

interface ProviderProps {
    children: ReactNode;
}

const B2cProvider: FCWithChildren<ProviderProps> = ({ children }) => {
    const [pca, setPca] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffectAsync(async () => {
        const instance = await BaseDataManager.RequestManager.getB2CInstance();
        if (isNil(instance)) {
            throw new Error('PCA must be available to use b2c Provider');
        } else {
            setPca(instance);
        }
        setIsLoading(false);
    }, []);

    if (isLoading) {
        return <AppLoader />;
    }

    return <MsalProvider instance={pca}>{children}</MsalProvider>;
};

/**
 * Merge Auth Redirect Options with required values
 * @param requestOptions Options we want to merge with
 * @returns Prepped Auth Redirect Options
 */
export const getAuthRedirectRequestOptions = (requestOptions: RedirectRequest) => {
    const { extraQueryParameters, ...rest } = requestOptions;
    return {
        ...rest,
        extraQueryParameters: {
            template: Config.b2cTemplateName,
            ...extraQueryParameters,
        },
    };
};

/**
 * Wrapper for components to show for authenticated users
 */
export const AuthenticatedTemplate: FCWithChildren<ProviderProps> = ({ children }) => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const authRequest: RedirectRequest = getAuthRedirectRequestOptions({
        scopes: [],
        // A temporary fix to revent infinite redirect after switching org in firefox due to authority mismatch
        onRedirectNavigate: () => activeAccount?.homeAccountId.includes('signup_signin'),
    });
    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={authRequest}
        >
            {children}
        </MsalAuthenticationTemplate>
    );
};

/**
 * Wrapper for components to show for unauthenticated users
 */
export const UnauthenticatedTemplate: FCWithChildren<ProviderProps> = () => {
    return <Navigate to="/" replace />;
};

/**
 * Authentication provider that will load the authenticated session
 */
const AuthenticationProvider: FCWithChildren<ProviderProps> = ({ children }) => {
    const dispatch = useDispatch();
    const [hasTokenLoaded, setHasTokenLoaded] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffectAsync(async () => {
        try {
            const token = await getAuthToken();
            if (token) {
                const payload = JwtDecode<LoginResponseToken>(token);
                dispatch(AppActions.loadActiveUser(payload.omid));
                dispatch(AppActions.loadActiveUserHash(payload.userHash));
                dispatch(AppActions.loadActiveOrganization(payload.oid));
                dispatch(AppActions.loadActiveTenant(payload.tid));
                if (payload.iDisplayName)
                    dispatch(
                        AppActions.loadActiveImpersonator({
                            name: payload.iDisplayName,
                            id: payload.iid,
                        })
                    );
                dispatch(OrganizationMemberActions.loadOrganizationMemberFromToken(payload));
            }
            if (Config.appInsightsKey) appInsights.initialize(navigate, Config.appInsightsKey);
            if (Config.tagManagerKey) tagManager.initialize(Config.tagManagerKey);
        } catch (authError) {
            console.error('Authentication initialization error:', authError);
            setError('Failed to initialize authentication');
        } finally {
            setHasTokenLoaded(true);
        }
    }, []);

    if (!hasTokenLoaded) {
        return <AppLoader />;
    }

    if (error) {
        console.error('Authentication Provider Error:', error);
        // Even if there's an error, try to render the app
        return <>{children}</>;
    }

    return <B2cProvider>{children}</B2cProvider>;
};

export default AuthenticationProvider;
